import React, { SyntheticEvent } from 'react';
import { Grid, Stack } from '@mui/material'; // , Stack, Tooltip
import Link from 'atoms/Link/Link';
import {
  StyledCellText2,
  StyledDetailsText,
} from 'molecules/Table/styles/Text.styles';
import {
  StyledDetailsContainer,
  StyledInvoiceApproveButton,
  StyledLinkButton,
} from 'organisms/CloudOpsReportAdmin/CloudOpsReportAdmin.styles';

import { neutrals } from 'utils/styles/color';
import { palette } from 'utils/styles/variables';
import { currencyFormatter } from 'utils/helpers/currencyFormatter';
import { getServiceName } from 'organisms/InvoicesTable/helpers';
import { CONTRACT_TYPE } from 'utils/constants/common';

import { ErrorStateItem, Report } from 'organisms/CloudOpsReportAdmin/types';
import { Report as CustomerReport } from 'pages/CloudOpsReport/types';
import { formatHours, getInvoiceCreationDate } from 'utils/helpers/dates';

import { ReactComponent as InfoIcon } from 'assets/icons/info-filled.svg';
import dayjs from 'dayjs';

const getInvoiceInfo = (
  report: Report,
  handleApprove?: (e: SyntheticEvent<HTMLButtonElement>) => void,
  showErrors?: (rep: Report) => void
) => {
  if (report.invoice_number) {
    return `#${report.invoice_number}`;
  }

  if (report.error_state) {
    const allPmApproved = (report.error_state as ErrorStateItem[]).every(
      ({ property }) => property === 'pm_approved_time_log'
    );

    if (!allPmApproved) {
      return (
        // eslint-disable-next-line
        // @ts-ignore
        <Stack
          direction="row"
          gap={1}
          onClick={showErrors ? () => showErrors(report) : null}
        >
          Billing data incorrect
          <InfoIcon color={palette.error[200]} />
        </Stack>
      );
    }
  }

  const date = new Date();
  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth() + 1;

  if (report.month === currentMonth && report.year === currentYear) {
    return `Current value ${
      currencyFormatter(Number(report.total_amount || 0), report.currency) || ''
    }`;
  }

  // https://sprints.zoho.com/workspace/zazmic#P71/itemdetails/I2208
  if (
    report.year <= currentYear &&
    report.month < currentMonth &&
    report.total_amount === 0
  ) {
    return 'Total amount is 0.0';
  }

  if (report.project.invoice_creation_approval_required) {
    if (
      !report.invoice_creation_approval &&
      [
        CONTRACT_TYPE.FIXED_PRICE,
        CONTRACT_TYPE.MONTHLY_RETAINER,
        CONTRACT_TYPE.TIME_AND_MATERIAL_AT_COST,
      ].includes(report.contract_type) &&
      handleApprove
    ) {
      return (
        <StyledInvoiceApproveButton
          variant="contained"
          size="small"
          onClick={handleApprove}
        >
          Approve
        </StyledInvoiceApproveButton>
      );
    }
    if (
      report.pm_approval &&
      ![
        CONTRACT_TYPE.FIXED_PRICE,
        CONTRACT_TYPE.MONTHLY_RETAINER,
        CONTRACT_TYPE.TIME_AND_MATERIAL_AT_COST,
      ].includes(report.contract_type)
    ) {
      if (!report.invoice_creation_approval && handleApprove) {
        return (
          <StyledInvoiceApproveButton
            variant="contained"
            size="small"
            onClick={handleApprove}
          >
            Approve
          </StyledInvoiceApproveButton>
        );
      }
      if (report.invoice_creation_approval && report.last_approval) {
        return 'Invoice will be created shortly';
      }
    }
    if (
      !report.pm_approval &&
      ![CONTRACT_TYPE.FIXED_PRICE, CONTRACT_TYPE.MONTHLY_RETAINER].includes(
        report.contract_type
      )
    ) {
      return 'Time logs not approved';
    }
    if (report.invoice_creation_approval) {
      return 'Invoice will be created shortly';
    }
  } else {
    if (
      ![CONTRACT_TYPE.FIXED_PRICE, CONTRACT_TYPE.MONTHLY_RETAINER].includes(
        report.contract_type
      )
    ) {
      if (report.pm_approval) {
        if (report.last_approval) {
          const invoiceCreationDate = getInvoiceCreationDate(
            report.last_approval
          );
          const now = dayjs();

          return dayjs(invoiceCreationDate).isBefore(now)
            ? `Invoice generation on ${dayjs(invoiceCreationDate).format(
                'MMM D, YYYY [at] HH:mm'
              )}`
            : `Invoice will be created shortly`;
        }
      } else {
        return 'Time logs not approved';
      }
    } else {
      return 'Invoice will be created shortly';
    }

    return '';
  }

  return '';
};

const getInvoiceColor = (
  report: Report,
  handleApprove?: (e: SyntheticEvent<HTMLButtonElement>) => void
) => {
  if (report.invoice_number) {
    return report.invoice_qb_id ? palette.primary.main : neutrals[700];
  }

  if (report.error_state) {
    const allPmApproved = (report.error_state as ErrorStateItem[]).every(
      ({ property }) => property === 'pm_approved_time_log'
    );

    if (!allPmApproved) return palette.error[450];
  }

  const date = new Date();
  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth() + 1;

  if (report.month === currentMonth && report.year === currentYear) {
    return neutrals[700];
  }

  // https://sprints.zoho.com/workspace/zazmic#P71/itemdetails/I2208
  if (
    report.year <= currentYear &&
    report.month < currentMonth &&
    report.total_amount === 0
  ) {
    return neutrals[700];
  }

  if (report.project.invoice_creation_approval_required) {
    if (
      !report.invoice_creation_approval &&
      [CONTRACT_TYPE.FIXED_PRICE, CONTRACT_TYPE.MONTHLY_RETAINER].includes(
        report.contract_type
      ) &&
      handleApprove
    ) {
      return neutrals[700];
    }
    if (
      report.pm_approval &&
      ![CONTRACT_TYPE.FIXED_PRICE, CONTRACT_TYPE.MONTHLY_RETAINER].includes(
        report.contract_type
      )
    ) {
      if (!report.invoice_creation_approval && handleApprove) {
        return neutrals[700];
      }
      if (report.invoice_creation_approval && report.last_approval) {
        return neutrals[700];
      }
    }
    if (
      !report.pm_approval &&
      ![CONTRACT_TYPE.FIXED_PRICE, CONTRACT_TYPE.MONTHLY_RETAINER].includes(
        report.contract_type
      )
    ) {
      return palette.error[450];
    }
    if (report.invoice_creation_approval) {
      return neutrals[700];
    }
  } else {
    if (
      ![CONTRACT_TYPE.FIXED_PRICE, CONTRACT_TYPE.MONTHLY_RETAINER].includes(
        report.contract_type
      )
    ) {
      if (report.pm_approval) {
        if (report.last_approval) {
          const invoiceCreationDate = getInvoiceCreationDate(
            report.last_approval
          );
          const now = dayjs();

          return dayjs(invoiceCreationDate).isBefore(now)
            ? palette.success[600]
            : neutrals[700];
        }
      } else {
        return palette.error[450];
      }
    } else {
      return neutrals[700];
    }

    return neutrals[700];
  }

  return neutrals[700];
};

const renderRowDetailsLabelByType = (row: Report) => {
  const monthlyBudgetInHours = row.project.monthly_budget_in_hours || 0;
  const adjustmentAmount = row.adjustment_amount || 0;
  if (row.contract_type === CONTRACT_TYPE.CLOUDOPS) {
    return (
      <StyledDetailsContainer>
        <StyledDetailsText $divider>Total hours logged</StyledDetailsText>
        <StyledDetailsText>
          Allocate {formatHours(monthlyBudgetInHours || 0)} h fee
        </StyledDetailsText>
        <StyledDetailsText $divider>
          Overage {formatHours(row.overage_hours || 0)} h (
          {currencyFormatter(
            Number(row.project.over_budget_hourly_rate),
            row.currency
          )}
          /h) fee
        </StyledDetailsText>
        <StyledDetailsText $bold>Total fee</StyledDetailsText>
      </StyledDetailsContainer>
    );
  }
  if (
    [CONTRACT_TYPE.FIXED_PRICE, CONTRACT_TYPE.MONTHLY_RETAINER].includes(
      row.contract_type
    )
  ) {
    return (
      <StyledDetailsContainer sx={{ height: '100%', justifyContent: 'center' }}>
        <StyledDetailsText $divider>Fixed price fee</StyledDetailsText>
        <StyledDetailsText $bold>Total fee</StyledDetailsText>
      </StyledDetailsContainer>
    );
  }
  if (row.contract_type === CONTRACT_TYPE.TIME_AND_MATERIAL) {
    return (
      <StyledDetailsContainer sx={{ height: '100%', justifyContent: 'center' }}>
        <StyledDetailsText $divider={!adjustmentAmount}>
          Usage-based fee
        </StyledDetailsText>
        {adjustmentAmount ? (
          <StyledDetailsText $divider>Adjustment fee</StyledDetailsText>
        ) : null}
        <StyledDetailsText $bold>Total fee</StyledDetailsText>
      </StyledDetailsContainer>
    );
  }
  if (row.contract_type === CONTRACT_TYPE.PRORATED_FIXED_PRICE) {
    return (
      <StyledDetailsContainer sx={{ height: '100%', justifyContent: 'center' }}>
        <StyledDetailsText>Usage-based fee</StyledDetailsText>
        <StyledDetailsText $divider>Adjustment fee</StyledDetailsText>
        <StyledDetailsText $bold>Total fee</StyledDetailsText>
      </StyledDetailsContainer>
    );
  }
  if (row.contract_type === CONTRACT_TYPE.TIME_AND_MATERIAL_AT_COST) {
    return (
      <StyledDetailsContainer sx={{ height: '100%', justifyContent: 'center' }}>
        <StyledDetailsText $divider={!adjustmentAmount}>
          Usage-based fee
        </StyledDetailsText>
        {adjustmentAmount ? (
          <StyledDetailsText $divider>Adjustment fee</StyledDetailsText>
        ) : null}
        <StyledDetailsText $bold>Total fee</StyledDetailsText>
      </StyledDetailsContainer>
    );
  }
  if (row.contract_type === CONTRACT_TYPE.MIXED_PRICE) {
    return (
      <StyledDetailsContainer sx={{ height: '100%', justifyContent: 'center' }}>
        <StyledDetailsText>Usage-based fee</StyledDetailsText>
        <StyledDetailsText $divider={!adjustmentAmount}>
          Fixed price fee
        </StyledDetailsText>
        {adjustmentAmount ? (
          <StyledDetailsText $divider>Adjustment fee</StyledDetailsText>
        ) : null}
        <StyledDetailsText $bold>Total fee</StyledDetailsText>
      </StyledDetailsContainer>
    );
  }
  return (
    <StyledDetailsContainer sx={{ height: '100%', justifyContent: 'center' }}>
      <StyledDetailsText>Usage-based fee</StyledDetailsText>
      <StyledDetailsText $divider>Fixed price fee</StyledDetailsText>
      <StyledDetailsText $bold>Total fee</StyledDetailsText>
    </StyledDetailsContainer>
  );
};

const renderRowDetailsValueByType = (row: Report) => {
  const adjustmentAmount = row.adjustment_amount || 0;
  if (row.contract_type === CONTRACT_TYPE.CLOUDOPS) {
    return (
      <StyledDetailsContainer>
        <StyledDetailsText $divider>
          {formatHours(row.hours_tracked || 0)} h
        </StyledDetailsText>
        <StyledDetailsText>
          {currencyFormatter(Number(row.monthly_fee), row.currency)}
        </StyledDetailsText>
        <StyledDetailsText $divider>
          {currencyFormatter(Number(row.overage_amount || 0), row.currency)}
        </StyledDetailsText>
        <StyledDetailsText $bold>
          {currencyFormatter(Number(row.total_amount || 0), row.currency)}
        </StyledDetailsText>
      </StyledDetailsContainer>
    );
  }
  if (
    [CONTRACT_TYPE.FIXED_PRICE, CONTRACT_TYPE.MONTHLY_RETAINER].includes(
      row.contract_type
    )
  ) {
    return (
      <StyledDetailsContainer sx={{ height: '100%', justifyContent: 'center' }}>
        <StyledDetailsText $divider>
          {currencyFormatter(Number(row?.monthly_fee || 0), row.currency)}
        </StyledDetailsText>
        <StyledDetailsText $bold>
          {currencyFormatter(Number(row?.total_amount || 0), row.currency)}
        </StyledDetailsText>
      </StyledDetailsContainer>
    );
  }
  if (row.contract_type === CONTRACT_TYPE.TIME_AND_MATERIAL) {
    return (
      <StyledDetailsContainer sx={{ height: '100%', justifyContent: 'center' }}>
        <StyledDetailsText $divider={!adjustmentAmount}>
          {currencyFormatter(
            Number(row.monthly_hourly_total_amount || 0),
            row.currency
          )}
        </StyledDetailsText>
        {adjustmentAmount ? (
          <StyledDetailsText $divider>
            {currencyFormatter(
              Number(row.adjustment_amount || 0),
              row.currency
            )}
          </StyledDetailsText>
        ) : null}
        <StyledDetailsText $bold>
          {currencyFormatter(Number(row.total_amount || 0), row.currency)}
        </StyledDetailsText>
      </StyledDetailsContainer>
    );
  }
  if (row.contract_type === CONTRACT_TYPE.PRORATED_FIXED_PRICE) {
    return (
      <StyledDetailsContainer sx={{ height: '100%', justifyContent: 'center' }}>
        <StyledDetailsText>
          {currencyFormatter(
            Number(row.monthly_hourly_total_amount || 0),
            row.currency
          )}
        </StyledDetailsText>
        <StyledDetailsText $divider>
          {currencyFormatter(Number(row.adjustment_amount || 0), row.currency)}
        </StyledDetailsText>
        <StyledDetailsText $bold>
          {currencyFormatter(Number(row.total_amount || 0), row.currency)}
        </StyledDetailsText>
      </StyledDetailsContainer>
    );
  }
  if (row.contract_type === CONTRACT_TYPE.TIME_AND_MATERIAL_AT_COST) {
    return (
      <StyledDetailsContainer sx={{ height: '100%', justifyContent: 'center' }}>
        <StyledDetailsText $divider={!adjustmentAmount}>
          {currencyFormatter(
            Number(row?.monthly_hourly_total_amount || 0),
            row.currency
          )}
        </StyledDetailsText>
        {adjustmentAmount ? (
          <StyledDetailsText $divider>
            {currencyFormatter(
              Number(row?.adjustment_amount || 0),
              row.currency
            )}
          </StyledDetailsText>
        ) : null}
        <StyledDetailsText $bold>
          {currencyFormatter(Number(row.total_amount || 0), row.currency)}
        </StyledDetailsText>
      </StyledDetailsContainer>
    );
  }
  if (row.contract_type === CONTRACT_TYPE.MIXED_PRICE) {
    return (
      <StyledDetailsContainer sx={{ height: '100%', justifyContent: 'center' }}>
        <StyledDetailsText>
          {currencyFormatter(
            Number(row?.monthly_hourly_total_amount || 0),
            row.currency
          )}
        </StyledDetailsText>
        <StyledDetailsText $divider={!adjustmentAmount}>
          {currencyFormatter(Number(row?.monthly_fee || 0), row.currency)}
        </StyledDetailsText>
        {adjustmentAmount ? (
          <StyledDetailsText $divider>
            {currencyFormatter(
              Number(row?.adjustment_amount || 0),
              row.currency
            )}
          </StyledDetailsText>
        ) : null}
        <StyledDetailsText $bold>
          {currencyFormatter(Number(row.total_amount || 0), row.currency)}
        </StyledDetailsText>
      </StyledDetailsContainer>
    );
  }
  return (
    <StyledDetailsContainer sx={{ height: '100%', justifyContent: 'center' }}>
      <StyledDetailsText>
        {currencyFormatter(
          Number(row?.monthly_hourly_total_amount || 0),
          row.currency
        )}
      </StyledDetailsText>
      <StyledDetailsText $divider>
        {currencyFormatter(Number(row?.monthly_fee || 0), row.currency)}
      </StyledDetailsText>
      <StyledDetailsText $bold>
        {currencyFormatter(Number(row.total_amount || 0), row.currency)}
      </StyledDetailsText>
    </StyledDetailsContainer>
  );
};

const contractWithUsageBasedFee = [
  CONTRACT_TYPE.PRORATED_FIXED_PRICE,
  CONTRACT_TYPE.TIME_AND_MATERIAL,
  CONTRACT_TYPE.TIME_AND_MATERIAL_AT_COST,
  CONTRACT_TYPE.MIXED_PRICE,
];

const renderRowDetails = (
  row: Report,
  link?: string,
  onShowLogs?: () => void,
  onAdjustmentOpen?: () => void
) => (
  <Grid container spacing={0} sx={{ p: '8px 80px', minHeight: '112px' }}>
    <Grid item xs>
      <StyledDetailsContainer $firstCol>
        {onShowLogs &&
          contractWithUsageBasedFee.includes(row.contract_type) &&
          !row.manually_created && (
            <StyledLinkButton onClick={onShowLogs}>
              View time logs details
            </StyledLinkButton>
          )}
        {onAdjustmentOpen && row.adjustment_amount && !row.manually_created ? (
          <StyledLinkButton onClick={onAdjustmentOpen}>
            Adjustment time logs details
          </StyledLinkButton>
        ) : null}
        {link && (
          <Link to={link} target="_blank" rel="noopener noreferrer">
            {`Open project ${getServiceName(link)}`}
          </Link>
        )}
        {row?.quickbook_invoice_link ? (
          <Link
            to={row?.quickbook_invoice_link}
            target="_blank"
            rel="noopener noreferrer"
          >
            Quickbooks invoice
          </Link>
        ) : null}
      </StyledDetailsContainer>
    </Grid>
    <Grid item xs="auto">
      {renderRowDetailsLabelByType(row)}
    </Grid>
    <Grid item xs="auto" minWidth={120}>
      {renderRowDetailsValueByType(row)}
    </Grid>
  </Grid>
);

const getCustomerInvoiceInfo = (report: CustomerReport) => {
  if (report.invoice_number) {
    return `#${report.invoice_number}`;
  }

  const date = new Date();
  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth() + 1;

  if (report.month === currentMonth && report.year === currentYear) {
    return `Current value ${
      currencyFormatter(Number(report.total_amount || 0), report.currency) || ''
    }`;
  }

  // https://sprints.zoho.com/workspace/zazmic#P71/itemdetails/I2208
  if (
    report.year <= currentYear &&
    report.month < currentMonth &&
    report.total_amount === 0
  ) {
    return 'Total amount is 0.0';
  }

  return '';
};

const getCustomerInvoiceColor = (report: CustomerReport) => {
  if (report.invoice_number) {
    return report.invoice_qb_id ? palette.primary.main : neutrals[700];
  }

  const date = new Date();
  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth() + 1;

  if (report.month === currentMonth && report.year === currentYear) {
    return neutrals[700];
  }

  // https://sprints.zoho.com/workspace/zazmic#P71/itemdetails/I2208
  if (
    report.year <= currentYear &&
    report.month < currentMonth &&
    report.total_amount === 0
  ) {
    return neutrals[700];
  }

  return neutrals[700];
};

const renderRowDetailsLabel = (report: Report) => {
  if (report?.invoice_number && report?.manually_created) {
    return (
      <StyledCellText2
        sx={{
          fontWeight: '300 !important',
          fontSize: '11px !important',
          lineHeight: '14px !important',
        }}
      >
        Manually created
      </StyledCellText2>
    );
  }

  if (report?.invoice_qb_id && report?.approvar_name) {
    return (
      <StyledCellText2
        sx={{
          fontWeight: '300 !important',
          fontSize: '11px !important',
          lineHeight: '14px !important',
        }}
      >
        approved by {report?.approvar_name}
      </StyledCellText2>
    );
  }

  return null;
};

export {
  getInvoiceInfo,
  getInvoiceColor,
  renderRowDetails,
  getCustomerInvoiceInfo,
  getCustomerInvoiceColor,
  renderRowDetailsLabel,
};
